import { useCallback, useEffect, useMemo, useState } from 'react';
import { studioApi } from '../data/studioApi';
import { nextPlan } from '../enums/plans';
import useStableActions from './useStableActions';

// Plans and credit packs (contract 1.8.0, `features.payments`). Holds the catalog and
// the subscription of a ready creator, and opens the plan dialog: an upgrade to the
// next plan, or credit packs once there is no plan above. The purchase itself (Stripe
// embedded checkout, or the card on file for an upgrade) lives in dialogs/PlanDialog.
export default function usePayments({ account, features, setModal, refreshUsage, loadAccount, mounted }) {
  const [catalog, setCatalog] = useState(null);
  const [subscription, setSubscription] = useState(null);
  // `off` (payments off, or no ready creator) · `loading` · `ready` · `error` (first read failed).
  const [status, setStatus] = useState('off');
  const creator = account.status === 'ready' ? account.creator : null;
  const live = !!features.payments && !!creator && creator.onboarding_status !== 'pending';
  const creatorId = creator?.id;

  const load = useCallback(async () => {
    const [plans, current] = await Promise.all([studioApi.plans(), studioApi.subscription()]);
    if (!mounted.current) return null;
    setCatalog(plans);
    setSubscription(current);
    setStatus('ready');
    return current;
  }, [mounted]);
  // A failed read only hides the offers; nothing else in the Studio depends on them.
  // The plan page shows it (`status: error`) and offers `retry`.
  const firstLoad = useCallback(() => {
    setStatus('loading');
    return load().catch(() => { if (mounted.current) setStatus('error'); return null; });
  }, [load, mounted]);
  useEffect(() => {
    if (!live) { setCatalog(null); setSubscription(null); setStatus('off'); return; }
    firstLoad();
  }, [live, creatorId, firstLoad]);

  const enabled = live && !!catalog && !!subscription;
  const plan = subscription?.plan || 'free';
  const currency = catalog?.default_currency || null;
  const planInfo = useCallback(sku => catalog?.plans.find(item => item.plan === sku) || null, [catalog]);
  const next = enabled && planInfo(nextPlan(plan)) ? nextPlan(plan) : null;
  const priceOf = sku => planInfo(sku)?.prices.find(price => price.currency === currency)?.unit_amount ?? null;
  const pack = catalog?.credit_pack || null;
  const packPrice = pack?.prices.find(price => price.currency === currency)?.unit_amount ?? null;

  const openUpgrade = (target = next, reason = null) => { if (target) setModal({ type: 'plan', kind: 'subscription', plan: target, reason }); };
  const openCredits = (quantity = null, reason = null) => setModal({ type: 'plan', kind: 'credits', quantity, reason });
  // No credits left: the next plan while there is one, otherwise credit packs.
  const outOfCredits = () => {
    if (!enabled) return false;
    if (next) openUpgrade(next, 'credits'); else openCredits(null, 'credits');
    return true;
  };
  // After a purchase. A new plan also changes the Studio's features (trending topics),
  // so the whole account is read again; a pack only moves the balance.
  const refresh = async ({ planChanged = false } = {}) => {
    await Promise.all([load().catch(() => null), planChanged ? loadAccount() : refreshUsage()]);
  };

  const actions = useStableActions({ priceOf, openUpgrade, openCredits, outOfCredits, refresh });
  // One identity while nothing changed: the shell and the composer are memoized on `payments`.
  return useMemo(() => ({ enabled, status, retry: firstLoad, catalog, subscription, setSubscription, plan, nextPlan: next, currency, planInfo, pack, packPrice, ...actions }),
    [enabled, status, firstLoad, catalog, subscription, plan, next, currency, planInfo, pack, packPrice, actions]);
}
